'use client'

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslations } from 'next-intl'
import OptimizedProductImage from '@/components/products/OptimizedProductImage'

interface ImageGalleryClientProps {
  mainImage: string
  additionalImages: string[]
}

export default function ImageGalleryClient({ 
  mainImage, 
  additionalImages = [] 
}: ImageGalleryClientProps) {
  const t = useTranslations();
  const [imageError, setImageError] = useState<Record<string, boolean>>({});
  const [currentImage, setCurrentImage] = useState(mainImage);
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Combine main image with additional images and filter out any that have errors
  const allImages = [mainImage, ...additionalImages]
    .filter(Boolean)
    .filter(img => !imageError[img]);
  
  // Update current image when props change or errors occur
  useEffect(() => {
    if (allImages.length > 0) {
      // If current image is not in valid images or has error, switch to first valid
      if (!allImages.includes(currentImage) || imageError[currentImage]) {
        setCurrentImage(allImages[0]);
        setActiveIndex(0);
      }
    }
  }, [allImages, currentImage, imageError]);
  
  // If there are no valid images, show default
  if (allImages.length === 0) {
    return (
      <div className="w-full h-full flex items-center justify-center rounded-lg">
        <div className="text-gray-400">No image available</div>
      </div>
    );
  }
  
  return (
    <div className="w-full flex flex-col">
      {/* Main image display */}
      <div className="relative w-full h-[400px] md:h-[500px] lg:h-[600px] overflow-hidden rounded-2xl bg-surface-hover shadow-2xl mb-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImage}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full h-full relative"
          >
            <OptimizedProductImage
              src={currentImage}
              alt={t('common.product')}
              fill
              sizes="(max-width: 768px) 100vw, 50vw"
              priority
              className="object-contain"
              showSpaceIcon={true}
              watermarkSize="large"
              onError={() => {
                // Mark this image as having an error
                setImageError(prev => ({ ...prev, [currentImage]: true }));
                
                // If this was the current image, switch to the first valid one
                if (currentImage === allImages[activeIndex]) {
                  const nextValidIndex = allImages.findIndex((img, idx) => idx !== activeIndex && !imageError[img]);
                  if (nextValidIndex >= 0) {
                    setCurrentImage(allImages[nextValidIndex]);
                    setActiveIndex(nextValidIndex);
                  }
                }
              }}
            />
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Thumbnails */}
      {allImages.length > 1 && (
        <div className="flex flex-wrap gap-2 m-1 items-center">
          {allImages.map((img, index) => (
            <button
              key={`${img}-${index}`}
              onClick={() => {
                console.log('Thumbnail clicked:', img, index, 'Current:', currentImage); // Debug log
                setCurrentImage(img);
                setActiveIndex(index);
              }}
              className={`relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 border-2 transition-all ${
                currentImage === img ? 'border-primary scale-105 opacity-100' : 'border-transparent opacity-70 hover:opacity-90'
              }`}
            >
              <OptimizedProductImage
                src={img}
                alt={`Thumbnail ${index + 1}`}
                fill
                sizes="64px"
                className="object-cover"
                showSpaceIcon={false}
                onError={() => {
                  // Mark this thumbnail as having an error
                  setImageError(prev => ({ ...prev, [img]: true }));
                }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
} 